// Card "Novedades de MiPAE": un aviso COMPACTO que se muestra UNA vez por
// edición del catálogo (lib/queCambio.js) y se reconoce por CUENTA
// (lib/novedadesCuenta.js). Sobria y descartable, jamás modal (regla del
// fundador) y jamás a mitad de turno (debeMostrarQueCambio). Responde el
// feedback real de colegas: "muchos cambios y no se entiende" (31-jul), y la
// auditoría editorial del 2026-09-28 (E10: el bloque de diez novedades
// reaparecía y su extensión interrumpía el trabajo).
//
// Contrato:
//   - No pinta nada hasta RESOLVER el estado de la cuenta: así no aparece y
//     desaparece en cada carga.
//   - La X y "Entendido" reconocen lo mismo; "Ver novedades" solo despliega el
//     detalle y NO reconoce nada.
//   - Reconocer es idempotente: el cierre se refleja al tiro y un montaje
//     repetido o una respuesta tardía no reabren el aviso.
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import BenitoAvatar from "../BenitoAvatar";
import NovedadesDetalle from "../NovedadesDetalle";
import AvisoLoNuevo from "../membresias/AvisoLoNuevo";
import { novedadesPendientes, debeMostrarQueCambio, cuerpoAviso } from "../../lib/queCambio";
import { resolverNovedadesVistas, novedadesResueltas, marcarNovedadesVistas } from "../../lib/novedadesCuenta";
import { cargarRegistros } from "../../lib/registroTurnoStore";
import { trackEvent } from "../../lib/eventTracking";
import { accionAviso } from "../../lib/invitacionMembresias";
import { isNative } from "../../lib/platform";

// La invitación del 25 toma la forma de AvisoLoNuevo: una hoja que sube una
// vez al abrir (la "C" que eligió Sebastián el 20-oct, única excepción a
// "jamás modal"). Kill-switch: con el flag en 'false' vuelve la card compacta.
const AVISO_LO_NUEVO_ENABLED = process.env.REACT_APP_AVISO_LO_NUEVO_ENABLED !== "false";
// La hoja vuelve una vez el 25 con el botón de la prueba (lib/queCambio.js).
const ID_PRUEBA_25 = "lo_nuevo_25_prueba";

export default function QueCambioCard({
  userId, modulo = "home", className = "",
  profile = null, onLoNuevo, onPerfil, onTrialIniciado, onTerminos,
}) {
  const [oculto, setOculto] = useState(false);
  const [detalle, setDetalle] = useState(false);
  // Si la cuenta ya se resolvió en esta sesión (App la hidrata al entrar), la
  // card nace con su estado listo; si no, espera a la resolución.
  const [estado, setEstado] = useState(() => (
    novedadesResueltas(userId)
      ? { listo: true, pendientes: novedadesPendientes(userId) }
      : { listo: false, pendientes: [] }
  ));

  useEffect(() => {
    if (!userId) return undefined;
    let vivo = true;
    resolverNovedadesVistas(userId).then(() => {
      if (vivo) setEstado({ listo: true, pendientes: novedadesPendientes(userId) });
    });
    return () => { vivo = false; };
  }, [userId]);

  const { listo, pendientes } = estado;

  // La vuelta de la hoja el 25 (`lo_nuevo_25_prueba`) existe para ofrecer la
  // prueba. Si es lo único pendiente y a esta persona no hay prueba que
  // ofrecerle (TENS, con membresía, con la prueba usada), se reconoce sin
  // mostrar nada: volver a subirle la hoja sería ruido. Sin perfil no se puede
  // saber y no se muestra (ni se reconoce).
  // La decisión se toma UNA vez, al resolver: si la persona activa la prueba
  // desde la hoja, el perfil nuevo ya no tiene "prueba que ofrecer" y, sin
  // congelarla, la hoja se cerraría encima del "Prueba activa" (revisión del
  // 20-oct). Sin perfil todavía se espera a tenerlo.
  const soloPrueba = listo && pendientes.length > 0 && pendientes.every((n) => n.id === ID_PRUEBA_25);
  const ofertaRef = useRef(null);
  if (soloPrueba && profile && ofertaRef.current === null) {
    ofertaRef.current = accionAviso(profile, { nativo: isNative }).id === "prueba";
  }
  const sinOferta = soloPrueba && ofertaRef.current !== true;
  const reconocerSinMostrar = soloPrueba && ofertaRef.current === false && !!userId;
  useEffect(() => {
    if (!reconocerSinMostrar) return;
    setOculto(true);
    marcarNovedadesVistas(userId).then((cuenta) => {
      trackEvent("que_cambio_visto", { modulo, payload: { orden: Math.max(...pendientes.map((n) => n.orden)), cuenta, sin_mostrar: true } });
    });
  }, [reconocerSinMostrar]); // eslint-disable-line react-hooks/exhaustive-deps

  let mostrar = false;
  if (listo) {
    try {
      mostrar = debeMostrarQueCambio({ pendientes, registros: cargarRegistros(userId) });
    } catch {
      mostrar = false;
    }
  }

  if (oculto || !mostrar || sinOferta) return null;

  const entendido = () => {
    // Primero se cierra (idempotente); después se persiste, y si la cuenta
    // falla el espejo local conserva el cierre para esta sesión.
    setOculto(true);
    const orden = Math.max(...pendientes.map((n) => n.orden));
    marcarNovedadesVistas(userId).then((cuenta) => {
      // Catálogo cerrado: a telemetría viaja SOLO el orden más alto visto y si
      // la cuenta quedó al día (para medir cuántos cierres viven solo en el
      // dispositivo).
      trackEvent("que_cambio_visto", { modulo, payload: { orden, cuenta } });
    });
  };

  // La invitación solo con un perfil y un destino: sin ellos (MiPAE en turno,
  // una prueba) la card sigue siendo la compacta de siempre.
  if (AVISO_LO_NUEVO_ENABLED && profile && onLoNuevo && pendientes.some((n) => n.id === "lo_nuevo_25" || n.id === ID_PRUEBA_25)) {
    return (
      <AvisoLoNuevo
        profile={profile}
        pendientes={pendientes}
        onEntendido={entendido}
        onLoNuevo={onLoNuevo}
        onPerfil={onPerfil}
        onTrialIniciado={onTrialIniciado}
        onTerminos={onTerminos}
      />
    );
  }

  const verNovedades = () => {
    setDetalle((v) => !v);
    if (!detalle) trackEvent("que_cambio_abierto", { modulo, payload: { origen: "card" } });
  };

  return (
    <section className={`rounded-tk-md border border-accent-border bg-accent-bg/40 p-4 ${className}`} aria-label="Novedades de MiPAE">
      <div className="flex items-start gap-3">
        <BenitoAvatar size={36} className="flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-small font-semibold text-ink font-sans">Novedades de MiPAE</p>
          <p className="mt-0.5 text-small text-ink-muted font-sans leading-snug">{cuerpoAviso(pendientes)}</p>
        </div>
        <button
          type="button"
          onClick={entendido}
          aria-label="Cerrar novedades"
          className="flex-shrink-0 -mt-1 -mr-1 grid h-9 w-9 place-items-center rounded-full text-ink-faint hover:text-ink-muted transition-colors"
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>
      {detalle && <NovedadesDetalle novedades={pendientes} className="mt-3" />}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={verNovedades}
          aria-expanded={detalle}
          className="flex-1 min-h-[44px] rounded-tk-sm border border-edge bg-paper px-3 py-2 text-small font-semibold text-ink-muted font-sans hover:border-accent-border hover:text-accent transition-colors"
        >
          {detalle ? "Ocultar detalle" : "Ver novedades"}
        </button>
        <button
          type="button"
          onClick={entendido}
          className="flex-1 min-h-[44px] rounded-tk-sm border border-accent-border bg-paper px-3 py-2 text-small font-semibold text-accent font-sans hover:bg-accent-bg transition-colors"
        >
          Entendido
        </button>
      </div>
    </section>
  );
}
